import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { ScrollArea } from 'app/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { useMediaSlice } from 'app/pages/Media/slice';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';

import { PlusIcon, TrashIcon, X } from 'lucide-react';
import { mediaBaseUrl } from 'utils/api/endpoints';
import { useCourseSlice } from '../../slice';

export function CategoriesForm() {
  const form = useFormContext();

  const { useLazyGetCourseCategoriesQuery } = useCourseSlice();
  const [
    getCategories,
    {
      data: CategoriesData,
      error: CategoriesError,
      isLoading: CategoriesLoading,
    },
  ] = useLazyGetCourseCategoriesQuery();

  useEffect(() => {
    getCategories({});
  }, [getCategories]);

  const { useUploadMediaMutation } = useMediaSlice();
  const [
    uploadFile,
    { isLoading: isUploadingImage, error: fileUploadingError },
  ] = useUploadMediaMutation();

  const handleUploadImageFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('products', file);

    try {
      const response = await uploadFile(formData).unwrap();
      form.setValue('image', response?.response?.products?.[0] || '');
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('File Upload Error:', err);
      toast.error('Image upload failed. Please try again.');
    }
  };

  if (CategoriesError) {
    toast.error('Failed to load categories. Please try again later.');
  }

  if (fileUploadingError) {
    toast.error('File upload error occurred. Please try again.');
  }

  const categoriesOptions =
    CategoriesData?.response?.data?.map((category: any) => ({
      label: category.category_name,
      value: category.category_id.toString(),
    })) || [];

  const parentCategoryOptions = [
    { label: 'No Parent', value: '0' },
    ...categoriesOptions,
  ];

  const handleDeleteImage = () => {
    form.setValue('image', '');
  };

  return (
    <ScrollArea className="h-[330px]">
      <div className="px-4 space-y-4">
        {/* Category Name */}
        <div>
          <Label htmlFor="category_name">Category Name</Label>
          <Input
            id="category_name"
            placeholder="Enter category name"
            {...form.register('category_name', {
              required: 'Category name is required',
            })}
            onChange={e => {
              form.setValue('slug', e.target.value);
            }}
          />
        </div>

        {/* Slug */}
        <div>
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            placeholder="Enter slug"
            {...form.register('slug', {
              required: 'Slug is required',
            })}
          />
        </div>

        {/* Parent Category */}
        <div>
          <Label htmlFor="parent_category_id">Parent Category</Label>
          <Select
            value={form.watch('parent_category_id')}
            onValueChange={value => form.setValue('parent_category_id', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a parent category" />
            </SelectTrigger>
            <SelectContent>
              {parentCategoryOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Image Upload */}
        <div>
          <Label htmlFor="image" className="font-medium text-sm">
            Image
          </Label>
          <div className="flex items-center justify-center mt-3">
            <Input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleUploadImageFile}
              aria-label="Upload category image"
            />
            {isUploadingImage && (
              <p className="text-sm text-gray-500">Uploading...</p>
            )}
          </div>
          {form.watch('image') && (
            <div className="flex items-center relative justify-center mt-5 border rounded-md p-2 w-32 h-32">
              <img
                src={mediaBaseUrl + '/products/' + form.watch('image')}
                alt="Category Image"
                className="object-contain w-full h-full"
              />
              <div
                className="bg-red-500 w-6 h-6 flex justify-center items-center  rounded-full p-1 absolute -right-1 -top-1"
                onClick={handleDeleteImage}
              >
                <X className="w-5 h-5 text-white cursor-pointer" />
              </div>
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
